import { IContractData } from "@/apps/credit-line/models/IContractData";
import { NumericConverter } from "@/shared-helpers/numeric-converter.helper";

export interface IMCDocService<T> {
  replaceTemplateVars(template: string, data: T): string;
  buildContract(template: string): string;
  buildTyC(template: string): string;
}

export class MXDocService implements IMCDocService<IContractData> {
  contractData: IContractData;

  constructor(contractData: IContractData) {
    this.contractData = contractData;
  }

  replaceTemplateVars(template: string, data: IContractData): string {
    try {
      return template.replace(/{{\s*(\w+)\s*}}/g, (match, key) => {
        return key in data ? String(data[key]) : match;
      });
    } catch (error) {
      console.log("error", error);
      return "";
    }
  }

  buildContract(template: string): string {
    return this.replaceTemplateVars(template, this.contractData);
  }

  buildTyC(template: string): string {
    return this.replaceTemplateVars(template, this.contractData);
  }
}

export class CODocService implements IMCDocService<IContractData> {
  contractData: IContractData;
  converter: NumericConverter;

  constructor(contractData: IContractData) {
    this.contractData = contractData;
    this.converter = new NumericConverter();
  }

  replaceTemplateVars(template: string, data: IContractData): string {
    return template.replace(/{{\s*(\w+)\s*}}/g, (match, key) => {
      return key in data ? String(data[key]) : match;
    });
  }

  private getWeeksLetters() {
    try {
      return this.converter.numberToWords(
        Number(this.contractData?.approvedCreditLine)
      );
    } catch (error) {
      console.log("getWeeksLetters", error);
      return "XXX";
    }
  }

  private getAmountLetters() {
    try {
      return this.converter.numberToWords(
        Number(this.contractData?.paymentTerm)
      );
    } catch (error) {
      console.log("getAmountLetters", error);
      return "YYY";
    }
  }

  private getDeviceBrand() {
    return this.contractData.makeModel.toString().split(" ")[0];
  }

  private getDeviceModel() {
    return this.contractData.makeModel.toString().split(" ")[1];
  }

  private getInsurancePercentage() {
    return (
      ((Number(this.contractData?.insuranceFeeRate) ?? 0) * 100).toFixed(2) +
      "%"
    );
  }

  private getAvailabilityFeePercentage() {
    return (
      ((Number(this.contractData?.availabilityFeeRate) ?? 0) * 100).toFixed(2) +
      "%"
    );
  }

  buildContract(template: string): string {
    const amountInLetters = this.getWeeksLetters();
    const weeksAmountLetters = this.getAmountLetters();
    const deviceBrand = this.getDeviceBrand();
    const deviceModel = this.getDeviceModel();

    const enhancedData = {
      ...this.contractData,
      amountInLetters,
      weeksAmountLetters,
      deviceBrand,
      deviceModel,
    } as IContractData;

    return this.replaceTemplateVars(template, enhancedData);
  }

  buildTyC(template: string): string {
    const insurancePercentage = this.getInsurancePercentage();
    const availabilityPercentage = this.getAvailabilityFeePercentage();

    const enhancedData = {
      ...this.contractData,
      insurancePercentage,
      availabilityPercentage,
    } as IContractData;

    return this.replaceTemplateVars(template, enhancedData);
  }
}

export class PEDocService implements IMCDocService<IContractData> {
  contractData: IContractData;
  converter: NumericConverter;

  constructor(contractData: IContractData) {
    this.contractData = contractData;
    this.converter = new NumericConverter();
  }

  replaceTemplateVars(template: string, data: IContractData): string {
    return template.replace(/{{\s*(\w+)\s*}}/g, (match, key) => {
      return key in data ? String(data[key]) : match;
    });
  }

  buildContract(template: string): string {
    return this.replaceTemplateVars(template, this.contractData);
  }

  buildTyC(template: string): string {
    return template;
  }
}
